import { Link } from "react-router-dom";
import { ChevronLeft, ImagePlus, Shield } from "lucide-react";

// Shared furniture for every club room. All colour comes from CSS variables set by ClubRoomPage,
// so these are club-agnostic — a new club is a schema, never new JSX.

export const DAYS = [
  ["MON", "Monday"], ["TUE", "Tuesday"], ["WED", "Wednesday"], ["THU", "Thursday"],
  ["FRI", "Friday"], ["SAT", "Saturday"], ["SUN", "Sunday"],
];
export const NOTICE_KINDS = [["NEWS", "News"], ["OFFER", "Offer"], ["SERIES", "Series"]];

export const ClubTopBar = ({ club, backTo = "/lobby" }) => (
  <header className="sticky top-0 z-30 flex items-center justify-between gap-4 px-5 md:px-10 py-3 club-bar"
    data-testid="club-topbar">
    <Link to={backTo} data-testid="club-back"
      className="club-chip flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em]">
      <ChevronLeft className="w-3 h-3" /> Players Room
    </Link>
    <img src={club.logo} alt={club.name} className="h-7 md:h-9 object-contain" data-testid="club-topbar-logo" />
    <Link to={`/${club.slug}/admin`} data-testid="club-admin-link"
      className="club-chip flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em]">
      <Shield className="w-3 h-3" /> <span className="hidden sm:inline">Club admin</span>
    </Link>
  </header>
);

// The club's own announcements, running across the top of their room.
export const ClubTicker = ({ lines }) => {
  const items = (lines || []).filter(Boolean);
  if (!items.length) return null;
  const run = [...items, ...items, ...items, ...items];
  return (
    <div className="club-ticker overflow-hidden py-3" data-testid="club-ticker">
      <div className="club-ticker-track flex gap-12 whitespace-nowrap">
        {run.map((t, i) => <span key={i} className="club-ticker-item text-lg md:text-2xl uppercase">{t}</span>)}
      </div>
    </div>
  );
};

export const ClubSection = ({ id, title, note, kicker, children, testId }) => (
  <section id={id} className="flex flex-col gap-5" data-testid={testId}>
    <div className="flex flex-col gap-2">
      {kicker && <span className="club-kicker text-[10px] uppercase club-accent-text">{kicker}</span>}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <h2 className="club-display text-2xl md:text-4xl uppercase leading-none club-neon">{title}</h2>
        {note && <span className="text-xs club-soft">{note}</span>}
      </div>
      <div className="club-rule" />
    </div>
    {children}
  </section>
);

// A lit signboard — bulbs top and bottom, inner neon frame. The room's hero furniture.
export const SignBoard = ({ children, className = "", delay = 0, testId }) => (
  <div className={`club-sign club-rise p-6 md:p-8 ${className}`} style={{ animationDelay: `${delay}ms` }}
    data-testid={testId}>
    <span className="club-bulbs club-bulbs-top" />
    <span className="club-bulbs club-bulbs-bottom" />
    <div className="relative">{children}</div>
  </div>
);

export const LiveLamp = ({ label = "On now" }) => (
  <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] club-accent-text">
    <span className="club-lamp" /> {label}
  </span>
);

// An image the club has not uploaded yet — the slot still has to read as deliberate, not broken.
export const PosterSlot = ({ src, label, ratio = "3 / 4", fit = "contain", testId }) => (
  <div className="club-frame relative overflow-hidden" style={{ aspectRatio: ratio }} data-testid={testId}>
    {src
      ? <img src={src} alt={label} className={`absolute inset-0 w-full h-full ${fit === "cover" ? "object-cover" : "object-contain"}`} loading="lazy" />
      : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 club-slot">
          <ImagePlus className="w-5 h-5" />
          <span className="text-[10px] uppercase tracking-[0.25em]">{label}</span>
          <span className="text-[10px] club-soft">Club uploads this</span>
        </div>
      )}
  </div>
);
